import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Zap, Server, CheckCircle2, Copy, Check, ExternalLink, Play, ArrowRight, Home } from 'lucide-react';
import CustomCursor from './CustomCursor';

const PlayPage = () => {
    const [copiedIP, setCopiedIP] = useState(false);

    const copyIP = () => {
        navigator.clipboard.writeText('play.pokemovie.fr');
        setCopiedIP(true);
        setTimeout(() => setCopiedIP(false), 2000);
    };

    const steps = [
        {
            number: "01",
            title: "Télécharge CurseForge",
            description: "Le launcher officiel pour installer facilement Cobblemon",
            icon: <Download className="w-8 h-8" />,
            color: "#DC0A2D",
            details: [
                "Rends-toi sur curseforge.com",
                "Télécharge le launcher CurseForge",
                "Installe-le et ouvre l'application",
                "Crée un compte ou connecte-toi"
            ],
            image: "/images/curseforge.jpg",
            ctaText: "Télécharger CurseForge",
            ctaLink: "https://www.curseforge.com/download/app"
        },
        {
            number: "02",
            title: "Installe Cobblemon Academy",
            description: "Le mod qui transforme Minecraft en aventure Pokémon",
            icon: <Zap className="w-8 h-8" />,
            color: "#FFCB05",
            details: [
                "Clique sur 'Minecraft' dans CurseForge",
                "Recherche 'Cobblemon Academy' dans la barre de recherche",
                "Clique sur 'Install' sur le mod Cobblemon Academy",
                "Attends que l'installation se termine"
            ],
            image: "/images/cobblemon-academy.png",
            ctaText: "Voir le Mod",
            ctaLink: "https://www.curseforge.com/minecraft/modpacks/cobblemon-academy"
        },
        {
            number: "03",
            title: "Connecte-toi au Serveur",
            description: "Rejoins l'aventure PokeMovie maintenant !",
            icon: <Server className="w-8 h-8" />,
            color: "#3B4CCA",
            details: [
                "Lance Minecraft avec le profil Cobblemon",
                "Clique sur 'Multijoueur'",
                "Ajoute notre serveur : play.pokemovie.fr",
                "Double-clique pour te connecter !"
            ],
            image: "/images/pokemovie.jpg",
        }
    ];

    const requirements = [
        { label: "Minecraft Java Edition", value: "1.20.1+", icon: "⚡" },
        { label: "Version Cobblemon", value: "1.5.2", icon: "🎮" },
        { label: "RAM Recommandée", value: "4-6 GB", icon: "💾" },
        { label: "Connexion Internet", value: "Stable", icon: "🌐" }
    ];

    return (
        <div className="relative min-h-screen bg-white overflow-hidden">
            {/* Custom Pokeball Cursor */}
            <CustomCursor />

            {/* Navigation with Back Button */}
            <motion.nav
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b-4 border-red-600 shadow-xl"
            >
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <motion.a
                        href="/"
                        className="flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Home className="w-5 h-5" />
                        Retour Accueil
                    </motion.a>

                    <div className="flex items-center gap-4">
                        <div className="relative w-10 h-10">
                            {/* Pokeball Logo */}
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-2xl"></div>
                            <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-red-600"></div>
                            <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-white"></div>
                            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-900 transform -translate-y-1/2"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border border-gray-900"></div>
                        </div>
                        <div>
                            <div className="text-3xl font-black text-red-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                PokeMovie
                            </div>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">Serveur Cobblemon</div>
                        </div>
                    </div>
                </div>
            </motion.nav>

            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Nunito:wght@400;600;700;800&display=swap');
        
        .step-progress {
          position: relative;
        }
        
        .step-progress::before {
          content: '';
          position: absolute;
          top: 0;
          left: 30px;
          width: 4px;
          height: 100%;
          background: linear-gradient(to bottom, #DC0A2D, #FFCB05, #3B4CCA);
          opacity: 0.2;
        }
        
        .step-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .step-card:hover {
          transform: translateX(10px);
        }
        
        .pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 30px rgba(220, 10, 45, 0.4);
          }
          50% {
            box-shadow: 0 0 50px rgba(220, 10, 45, 0.7);
          }
        }
        
        .float-animation {
          animation: float 3s ease-in-out infinite;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white pt-32 pb-20 px-6">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-32 h-32 border-8 border-white rounded-full"></div>
                    <div className="absolute bottom-20 right-20 w-48 h-48 border-8 border-white rounded-full"></div>
                    <div className="absolute top-1/2 left-1/3 w-24 h-24 border-8 border-white rounded-full"></div>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center"
                    >
                        <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                            className="inline-block mb-6"
                        >
                            <div className="w-24 h-24 relative float-animation">
                                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white to-gray-200 shadow-2xl"></div>
                                <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-white"></div>
                                <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-red-600"></div>
                                <div className="absolute top-1/2 left-0 right-0 h-2 bg-gray-900 transform -translate-y-1/2"></div>
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full border-4 border-gray-900 pulse-glow"></div>
                            </div>
                        </motion.div>

                        <h1 className="text-5xl lg:text-7xl font-black mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Rejoins <span className="text-yellow-400">PokeMovie</span>
                        </h1>

                        <p className="text-2xl lg:text-3xl text-red-100 mb-8 max-w-3xl mx-auto font-semibold">
                            Suis ce guide simple en 3 étapes et commence ton aventure Pokémon en 10 minutes !
                        </p>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
                        >
                            <div
                                onClick={copyIP}
                                className="group cursor-pointer flex items-center gap-4 px-8 py-5 rounded-2xl bg-white/10 backdrop-blur-sm border-4 border-white/30 hover:bg-white/20 transition-all"
                            >
                                <Server className="w-8 h-8 text-yellow-400" />
                                <code className="text-white font-mono font-black text-2xl">play.pokemovie.fr</code>
                                <motion.div
                                    initial={false}
                                    animate={{ scale: copiedIP ? 1 : 0 }}
                                >
                                    {copiedIP ? (
                                        <Check className="w-6 h-6 text-green-400" />
                                    ) : (
                                        <Copy className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
                                    )}
                                </motion.div>
                            </div>
                        </motion.div>

                        {/* Requirements Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                            {requirements.map((req, i) => (
                                <motion.div
                                    key={req.label}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.7 + i * 0.1 }}
                                    className="p-5 rounded-2xl bg-white/10 backdrop-blur-sm border-2 border-white/30"
                                >
                                    <div className="text-4xl mb-3">{req.icon}</div>
                                    <div className="text-lg font-bold text-white mb-1">{req.value}</div>
                                    <div className="text-sm text-red-200 font-semibold">{req.label}</div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Steps Section */}
            <section className="relative py-24 px-6">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-5xl lg:text-6xl font-black text-gray-900 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Installation <span className="text-red-600">Ultra-Simple</span>
                        </h2>
                        <p className="text-2xl text-gray-600 font-semibold">
                            3 étapes pour devenir un Maître Pokémon
                        </p>
                    </motion.div>

                    <div className="space-y-12 step-progress">
                        {steps.map((step, index) => (
                            <motion.div
                                key={step.number}
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.2 }}
                                className="step-card"
                            >
                                <div className="grid lg:grid-cols-2 gap-8 items-center">
                                    {/* Content */}
                                    <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                                        <div className="flex items-start gap-6">
                                            {/* Step Number */}
                                            <div
                                                className="flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center text-white font-black text-2xl shadow-2xl"
                                                style={{
                                                    background: `linear-gradient(135deg, ${step.color}, ${step.color}dd)`,
                                                    fontFamily: 'Montserrat, sans-serif'
                                                }}
                                            >
                                                {step.number}
                                            </div>

                                            <div className="flex-1">
                                                <div
                                                    className="w-14 h-14 rounded-xl flex items-center justify-center text-white mb-4 shadow-xl"
                                                    style={{ backgroundColor: step.color }}
                                                >
                                                    {step.icon}
                                                </div>

                                                <h3 className="text-4xl font-black text-gray-900 mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                                    {step.title}
                                                </h3>

                                                <p className="text-xl text-gray-600 font-semibold mb-6">
                                                    {step.description}
                                                </p>

                                                <ul className="space-y-3 mb-6">
                                                    {step.details.map((detail, i) => (
                                                        <motion.li
                                                            key={i}
                                                            initial={{ opacity: 0, x: -20 }}
                                                            whileInView={{ opacity: 1, x: 0 }}
                                                            viewport={{ once: true }}
                                                            transition={{ delay: 0.1 * i }}
                                                            className="flex items-center gap-3 text-gray-700 font-semibold"
                                                        >
                                                            <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                                                            <span className="text-lg">{detail}</span>
                                                        </motion.li>
                                                    ))}
                                                </ul>

                                                {step.ctaText && (
                                                    <motion.a
                                                        href={step.ctaLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-3 px-8 py-4 rounded-xl font-black text-lg shadow-xl hover:shadow-2xl transition-all group"
                                                        style={{
                                                            backgroundColor: step.color,
                                                            color: 'white',
                                                            fontFamily: 'Montserrat, sans-serif'
                                                        }}
                                                        whileHover={{ scale: 1.05, y: -2 }}
                                                        whileTap={{ scale: 0.95 }}
                                                    >
                                                        {step.ctaText}
                                                        <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                                    </motion.a>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Image */}
                                    <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                                        <motion.div
                                            whileHover={{ scale: 1.02 }}
                                            className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-gray-100"
                                        >
                                            <img
                                                src={step.image}
                                                alt={step.title}
                                                className="w-full h-96 object-cover"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                                            <div
                                                className="absolute top-6 right-6 w-16 h-16 rounded-2xl flex items-center justify-center text-white font-black text-2xl shadow-2xl"
                                                style={{
                                                    backgroundColor: step.color,
                                                    fontFamily: 'Montserrat, sans-serif'
                                                }}
                                            >
                                                {step.number}
                                            </div>
                                        </motion.div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Help Section */}
            <section className="relative py-20 px-6 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
                <div className="max-w-5xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center"
                    >
                        <div className="text-6xl mb-6">❓</div>
                        <h2 className="text-4xl lg:text-5xl font-black mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Besoin d'Aide ?
                        </h2>
                        <p className="text-xl text-blue-100 mb-8 font-semibold">
                            Notre communauté est là pour t'aider ! Rejoins notre Discord pour toute question.
                        </p>

                        <div className="grid md:grid-cols-3 gap-6">
                            {[
                                { icon: "💬", title: "Discord", desc: "Support 24/7", link: "https://discord.gg/3wS59h3Vbu" },
                                { icon: "📚", title: "Wiki", desc: "Guide complet", link: "/wiki" },
                                { icon: "🎥", title: "Tutoriels", desc: "Vidéos d'aide", link: "#" }
                            ].map((item, i) => (
                                <motion.a
                                    key={item.title}
                                    href={item.link}
                                    target="_blank"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1 }}
                                    whileHover={{ scale: 1.05, y: -5 }}
                                    className="p-8 rounded-2xl bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 transition-all"
                                >
                                    <div className="text-5xl mb-4">{item.icon}</div>
                                    <div className="text-2xl font-black mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                        {item.title}
                                    </div>
                                    <div className="text-blue-200 font-semibold">{item.desc}</div>
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="relative py-24 px-6 bg-gradient-to-br from-red-600 via-red-700 to-red-800">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                    >
                        <div className="text-7xl mb-8">🎮</div>
                        <h2 className="text-5xl lg:text-6xl font-black text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Prêt à Commencer ?
                        </h2>
                        <p className="text-2xl text-red-100 mb-10 font-semibold">
                            Plus de 850 dresseurs t'attendent déjà sur PokeMovie !
                        </p>
                        <motion.button
                            className="inline-flex items-center gap-4 px-12 py-6 rounded-2xl bg-white text-red-600 font-black text-2xl shadow-2xl hover:bg-yellow-400 hover:text-red-700 transition-all"
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                            whileHover={{ scale: 1.05, y: -3 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Play className="w-8 h-8" fill="currentColor" />
                            LANCER MINECRAFT
                            <ArrowRight className="w-8 h-8" />
                        </motion.button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default PlayPage;